import React, { useCallback, useEffect, useMemo, useState } from "react";

import { type MissingDataForm } from "pages/app/historical-practice-data-table-view/components/AddEditMissingDataDrawer.tsx";

type UseMissingDataTableSelectionParams = {
  // ids of every row that's currently eligible to be checked (already filtered/sorted by the caller)
  selectableRowKeys: string[];
  getMissingItemById: (id: string) => MissingDataForm | null;
  // selection resets whenever the selected year changes, since row ids aren't stable across years
  selectedYear: number | undefined;
  // optional gate for range-select/"select all" (e.g. AllFieldsDataTable's crop-type lock); defaults to allow-all
  isRowSelectable?: (item: MissingDataForm) => boolean;
};

// row checkbox selection (incl. shift-click range select), row-expansion, and "select all" state shared by
// MissingDataTable and AllFieldsDataTable - identical mechanics in both, only how rows/items are built differs
export const useMissingDataTableSelection = ({
  selectableRowKeys,
  getMissingItemById,
  selectedYear,
  isRowSelectable = () => true,
}: UseMissingDataTableSelectionParams) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<Set<string>>(new Set());
  const [selectedItems, setSelectedItems] = useState<Map<string, MissingDataForm>>(new Map());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState<boolean>(false);
  const [lastSelectedRow, setLastSelectedRow] = useState<{ index: number; cropId: string } | null>(null);

  const toggleSetItem = (key: string, setterFunction: React.Dispatch<React.SetStateAction<Set<string>>>) => {
    setterFunction((prevState) => {
      const newSet = new Set(prevState);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const onCheckBoxClicked = useCallback(
    (key: string, shiftPressed = false) => {
      const item = getMissingItemById(key);
      if (!item) {
        // eslint-disable-next-line no-console
        console.error(`Could not find item with the id ${key}`);
        return;
      }
      const clickedIndex = selectableRowKeys.indexOf(key);

      setSelectedItems((prevState) => {
        const newMap = new Map(prevState);
        const shouldSelect = !newMap.has(key);

        if (shiftPressed && lastSelectedRow !== null && clickedIndex !== -1) {
          const rangeStart = Math.min(lastSelectedRow.index, clickedIndex);
          const rangeEnd = Math.max(lastSelectedRow.index, clickedIndex);
          const rangeKeys = selectableRowKeys.slice(rangeStart, rangeEnd + 1);

          rangeKeys.forEach((rangeKey) => {
            if (shouldSelect) {
              const rangeItem = getMissingItemById(rangeKey);
              if (rangeItem && isRowSelectable(rangeItem)) newMap.set(rangeKey, rangeItem);
              return;
            }
            newMap.delete(rangeKey);
          });
        } else if (newMap.has(key)) {
          newMap.delete(key);
        } else {
          newMap.set(key, item);
        }

        setIsMultiSelectMode(newMap.size > 1);
        return newMap;
      });

      if (clickedIndex !== -1) setLastSelectedRow({ index: clickedIndex, cropId: key });
    },
    [getMissingItemById, selectableRowKeys, lastSelectedRow, isRowSelectable]
  );

  const selectedRowCount = useMemo(() => selectableRowKeys.filter((key) => selectedItems.has(key)).length, [selectableRowKeys, selectedItems]);
  const allRowsSelected = selectableRowKeys.length > 0 && selectedRowCount === selectableRowKeys.length;

  // Runs after query re-fetch: keeps selected items fresh and removes rows no longer in the table.
  useEffect(() => {
    if (selectedItems.size === 0) return;

    const visibleKeys = new Set(selectableRowKeys);
    const refreshedItems = new Map<string, MissingDataForm>();

    selectedItems.forEach((staleItem, key) => {
      if (visibleKeys.has(key)) {
        refreshedItems.set(key, getMissingItemById(key) ?? staleItem);
      }
    });

    setSelectedItems(refreshedItems);
  }, [selectableRowKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectAllClicked = useCallback(
    (checked: boolean) => {
      if (!checked) {
        setSelectedItems(new Map());
        setIsMultiSelectMode(false);
        return;
      }

      const allItems = new Map<string, MissingDataForm>();
      selectableRowKeys.forEach((rowKey) => {
        const item = getMissingItemById(rowKey);
        if (item && isRowSelectable(item)) allItems.set(rowKey, item);
      });

      setSelectedItems(allItems);
      setIsMultiSelectMode(allItems.size > 0);
    },
    [getMissingItemById, selectableRowKeys, isRowSelectable]
  );

  useEffect(() => {
    setSelectedItems(new Map());
    setIsMultiSelectMode(false);
    setLastSelectedRow(null);
  }, [selectedYear]);

  useEffect(() => {
    setLastSelectedRow((prev) => {
      if (!prev) return null;
      const newIndex = selectableRowKeys.indexOf(prev.cropId);
      return newIndex !== -1 ? { index: newIndex, cropId: prev.cropId } : null;
    });
  }, [selectableRowKeys]);

  return {
    expandedRowKeys,
    setExpandedRowKeys,
    toggleSetItem,
    selectedItems,
    setSelectedItems,
    isMultiSelectMode,
    setIsMultiSelectMode,
    onCheckBoxClicked,
    selectedRowCount,
    allRowsSelected,
    handleSelectAllClicked,
  };
};
